import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { FormInput } from './FormInputs';
import { FormContext } from './FormContext';
import clsx from 'clsx';
import { InputStatus } from '../inputs/InputStatus';

interface FormFieldInputContextValue {
  set: (name: string, input: FormInput) => void;
}

export const FormFieldInputContext = createContext<
  FormFieldInputContextValue | undefined
>(undefined);

type FieldStateSetter = (state: {
  message?: string;
  status: InputStatus;
}) => void;
type FieldState = { message?: string; status?: InputStatus };
type Children =
  | ReactNode
  | ((state: FieldState, setState: FieldStateSetter) => ReactNode);

interface Props {
  children: Children;
}

export function FormField(props: Props) {
  const inputRef = useRef<FormInput>(null);
  const [state, setState] = useState<FieldState>({});
  const formContext = useContext(FormContext);

  const children =
    typeof props.children === 'function'
      ? props.children(state, setState)
      : props.children;

  const setDefault = useCallback((message?: string) => {
    setState({ message, status: 'default' });
  }, []);

  const setError = useCallback((message?: string) => {
    setState({ message, status: 'error' });
  }, []);

  const setSuccess = useCallback((message?: string) => {
    setState({ message, status: 'success' });
  }, []);

  const setWarning = useCallback((message?: string) => {
    setState({ message, status: 'warning' });
  }, []);

  const formFieldContextValue = useMemo<FormFieldInputContextValue>(() => {
    return {
      set: (name, input) => {
        inputRef.current = input;
        formContext.setField(name, {
          input,
          setDefault,
          setError,
          setState,
          setSuccess,
          setWarning,
        });
      },
    };
  }, [formContext, setDefault, setError, setSuccess, setWarning]);

  return (
    <FormFieldInputContext.Provider value={formFieldContextValue}>
      {children}
    </FormFieldInputContext.Provider>
  );
}
